import { useCallback, useMemo } from 'react';
import { useIntl } from '@futrli/localisation';

import { StepType as StepTypeOrg } from '../hooks/use-upload-organisation';
import { StepType as StepTypeForecast } from '../hooks/use-upload-forecast';
import { UploadEntity } from './upload-entity';

interface UseFileUploadStatusParams {
  step: StepTypeOrg | StepTypeForecast;
  entity: UploadEntity;
  isInProgress: boolean;
  uploadSuccess: boolean;
  uploadFailed: boolean;
  file?: File;
}

const STEP_MESSAGE_MAP: Record<string, { id: string; defaultMessage: string }> = {
  upload: { id: 'common.uploading_file', defaultMessage: 'Uploading file' },
  scan: { id: 'predict.scanning_file', defaultMessage: 'Scanning file' },
  validation: { id: 'predict.validating_file', defaultMessage: 'Validating file structure' },
  sync: { id: 'predict.sync_org', defaultMessage: 'Syncing organisation data' },
  update: { id: 'predict.updating_forecast', defaultMessage: 'Updating forecast' }
};

const CREATE_MESSAGE_MAP: Record<UploadEntity, { id: string; defaultMessage: string }> = {
  [UploadEntity.Organisation]: { id: 'predict.creating_org', defaultMessage: 'Creating organisation' },
  [UploadEntity.Driver]: { id: 'predict.creating_driver', defaultMessage: 'Creating driver' },
  [UploadEntity.Forecast]: { id: 'predict.creating_forecast', defaultMessage: 'Creating forecast' }
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

export const useFileUploadStatus = ({
  step,
  entity,
  isInProgress,
  uploadSuccess,
  uploadFailed,
  file
}: UseFileUploadStatusParams) => {
  const intl = useIntl();

  const getUploadStatusMessage = useCallback((): string => {
    const descriptor = STEP_MESSAGE_MAP[step] ?? CREATE_MESSAGE_MAP[entity];
    return intl.formatMessage(descriptor);
  }, [intl, step, entity]);

  const uploadStatus = useMemo(() => {
    if (isInProgress && file) {
      return [
        {
          status: 'uploading' as const,
          filename: file.name,
          onAction: noop,
          message: getUploadStatusMessage()
        }
      ];
    }
    if (uploadSuccess && file) {
      return [
        {
          status: 'completed' as const,
          filename: file.name,
          onAction: noop,
          href: '#',
          underline: 'always' as const
        }
      ];
    }
    if (file && !uploadFailed) {
      return [
        { status: 'uploading' as const, filename: file.name, onAction: noop, progress: 100, message: ' ' }
      ];
    }
    return [];
  }, [isInProgress, uploadSuccess, uploadFailed, file, getUploadStatusMessage]);

  return { uploadStatus };
};
